import { Recurrence, SelfServiceSchedulePane, StaticInitializers } from '@tableau/self-service-schedules';
import { SelfServiceSchedulePaneType } from '@tableau/self-service-schedules/lib/src/components/SelfServiceSchedulePane';
import { SiteUrls } from '@tableau/shared-urls';
import { DialogFooterButtonsWidget, DialogPositioningBehavior, DialogType, DialogWidget, DropdownButtonType, HostedStackedElementContainer, IntegerStepperWidget, List, ListItem, SingleSelectDropdown, TabStyles, TextFieldWidget } from '@tableau/widgets';
import { ButtonType } from '@tableau/widgets/lib/src/widgets/Button/ButtonWidget';
import * as React from 'react';
import { style } from 'typestyle';
import { Messages } from '../../codegen/Localize';
import { IResult } from '../../ts/ServerApi/types/IResponse';
import { ServerService } from '../../ts/services/ServerService';
import { ServiceLocator } from '../ServiceLocator';

export interface AdminSelfServiceScheduleDialogProps {
    name?: string;
    recurrence?: Recurrence;
    type?: string;
    priority?: number;
    close: () => void;
    confirm: (state: AdminSelfServiceScheduleDialogState) => void;
}

export interface AdminSelfServiceScheduleDialogState {
    recurrence: Recurrence;
    name: string;
    type: string;
    priority: number;
    isNewSchedule: boolean;
    isParallel: boolean;
}

enum ExecutionType {
    Parallel= 'parallel',
    Serial= 'serial',
}

export class AdminSelfServiceScheduleDialog extends React.Component<AdminSelfServiceScheduleDialogProps, AdminSelfServiceScheduleDialogState> {

    public static async open(save: (scheduleState: AdminSelfServiceScheduleDialogState) => Promise<IResult>, recurrence?: Recurrence, name?: string, type?: string, priority?: number): Promise<IResult> {
        return new Promise((resolve, reject) => {
            const hostContainer = new HostedStackedElementContainer();
            const close = () => {
                hostContainer && hostContainer.remove();
            };
            const props: AdminSelfServiceScheduleDialogProps = {
                recurrence, name, type, priority, // Optional props
                close,
                confirm: (state: AdminSelfServiceScheduleDialogState) => {
                    // The confirm function should resolve the promise so that the caller to open can await on it and handle it
                    // Note that even when saving fails, it is expect the promise will have a "result" and not an "error".
                    // Specifically it will have a list of IError[] (see IResult)
                    close();
                    save(state)
                        .then(result => resolve(result))
                        .catch(error => reject(error));
                }
            };
            const dialog = React.createElement(AdminSelfServiceScheduleDialog, props);
            hostContainer.render(dialog);
        });
    }

    constructor(props: AdminSelfServiceScheduleDialogProps) {
        super(props);
        const resultRecurrence = props.recurrence || StaticInitializers.defaultRecurrence(15);
        this.state = {
            ...props,
            recurrence: resultRecurrence,
            name: props.name || '',
            type: props.type || 'Extract',
            isNewSchedule: !props.name,
            priority: props.priority || 50,
            isParallel: true,
        };
        this.updateRecurrence = this.updateRecurrence.bind(this);
    }

    // Its own method to avoid bloating the render method too much
    public renderFooter() {
        return <DialogFooterButtonsWidget
            buttons={[
                {
                    handleClick: () => this.props.close(),
                    buttonType: ButtonType.Outline,
                    children: Messages.adminSelfServiceSchedulesDialogCancel(),
                    testId: 'footer-cancel'
                },
                {
                    handleClick: () => this.props.confirm(this.state),
                    buttonType: ButtonType.Go,
                    children: this.state.isNewSchedule
                        ? Messages.adminSelfServiceSchedulesDialogConfirmCreate()
                        : Messages.adminSelfServiceSchedulesDialogConfirmUpdate(),
                    testId: 'footer-confirm',
                    disabled: this.state.name === ''
                }
            ]}
            testId="cancel-dialog-footer"
        />;
    }

    public updateRecurrence(newRecurrence: Recurrence) {
        this.setState({
            recurrence: newRecurrence,
        });
    }

    public getScheduleTypes(): ListItem[] {
        const results: ListItem[] = [];
        results.push({
            value: 'Extract',
            content: Messages.adminSelfServiceSchedulesDialogTypeExtractRefresh(),
        });
        results.push({
            value: 'Subscription',
            content: Messages.adminSelfServiceSchedulesDialogTypeSubscription(),
        });
        results.push({
            value: 'Flow',
            content: Messages.adminSelfServiceSchedulesDialogTypeFlow(),
        });
        return results;
    }

    public getParallelizationOptions(): ListItem[] {
        const results: ListItem[] = [];
        results.push({
            value: ExecutionType.Parallel,
            content: Messages.adminSelfServiceSchedulesDialogExecutionParallel(),
        });
        results.push({
            value: ExecutionType.Serial,
            content: Messages.adminSelfServiceSchedulesDialogExecutionSerial(),
        });
        return results;
    }

    public render(): React.ReactNode {
        const typeListItems = this.getScheduleTypes();
        const typeList = <List items={typeListItems} style={{ maxHeight: '200px', overflow: 'auto' }} />;
        const typeSelectedItem = typeListItems.find(i => i.value === this.state.type);
        const executionListItems = this.getParallelizationOptions();
        const executionList = <List items={executionListItems} style={{ maxHeight: '200px', overflow: 'auto' }} />;
        const executionSelectedItem = this.state.isParallel
            ? Messages.adminSelfServiceSchedulesDialogExecutionParallel()
            : Messages.adminSelfServiceSchedulesDialogExecutionSerial();
        const fieldStyle = {
            paddingBottom: 12,
            flex: 1,
            minWidth: 225,
            maxWidth: 225,
        };
        const server = ServiceLocator.get<ServerService>('ServerService');
        const userAccountSettingsLink = new SiteUrls({
            siteUrlName: server.sessionSiteUrlName(),
            queryString: window.location.search
        }).userSettingsUrl(server.sessionUserInfo().username, server.sessionUserInfo().domainName);
        const timeZoneDetails = {
            timeZonesEnabled: false,
            timeZoneDisplayName: server.userTimeZone().displayName,
            linkToUserSettings: userAccountSettingsLink,
        };
        return <DialogWidget
            testId="edit-schedule-inner"
            type={DialogType.NormalWithTitleBar}
            title={this.state.isNewSchedule
                ? Messages.adminSelfServiceSchedulesDialogCreateTitle()
                : Messages.adminSelfServiceSchedulesDialogEditTitle()
            }
            onCloseRequest={this.props.close}
            showCloseButton={true}
            positioningBehavior={DialogPositioningBehavior.Draggable}
            isModal={true}
            showFooter={true}
            handleGlassClick={this.props.close}
            isGlassTransparent={false}
            footerContent={this.renderFooter()}
            dialogWidth={500}
        >
            <div className={style({ display: 'flex', flexDirection: 'row', flexWrap: 'wrap'})}>
                <TextFieldWidget
                    label={Messages.adminSelfServiceSchedulesDialogName()}
                    onChange={e => this.setState({
                        name: e.target.value
                    })}
                    onClear={() => this.setState({
                        name: ''
                    })}
                    placeholder={Messages.adminSelfServiceSchedulesDialogNamePlaceholder()}
                    testId="schedule-name"
                    value={this.state.name}
                    kind="line"
                    className={style(fieldStyle)}
                />
                <SingleSelectDropdown
                    label={Messages.adminSelfServiceSchedulesDialogType()}
                    list={typeList}
                    onChange={(type: string) => this.setState({
                        type
                    })}
                    type={DropdownButtonType.Line}
                    value={this.state.type}
                    testId="schedule-type"
                    className={style({
                        ...fieldStyle,
                    })}
                    disabled={!this.state.isNewSchedule}
                >
                    {typeSelectedItem.content}
                </SingleSelectDropdown>
                <div
                    className={style({
                        ...fieldStyle,
                    })}
                >
                    <IntegerStepperWidget
                        max={100}
                        min={1}
                        start={this.state.priority}
                        testId="schedule-priority"
                        onValueUpdated={(priority?: number) => {
                            if (priority) {
                                this.setState({ priority });
                            }
                        }}
                        label={Messages.adminSelfServiceSchedulesDialogPriority()}
                        />
                </div>
                <SingleSelectDropdown
                    label={Messages.adminSelfServiceSchedulesDialogExecution()}
                    list={executionList}
                    onChange={(execution: string) => this.setState({
                        isParallel: execution === ExecutionType.Parallel
                    })}
                    type={DropdownButtonType.Line}
                    value={this.state.type}
                    testId="schedule-execution"
                    className={style(fieldStyle)}
                >
                    {executionSelectedItem}
                </SingleSelectDropdown>
            </div>
            <label
                id="schedule-dialog-sss-pane-label"
                htmlFor="schedule-dialog-sss-pane"
                style={{
                    color: TabStyles.Typography.Color.Secondary,
                    paddingBottom: 6
                }}
            >{Messages.adminSelfServiceSchedulesDialogFrequency()}</label>
            <div id="schedule-dialog-sss-pane">
                <SelfServiceSchedulePane
                    recurrenceModel={this.state.recurrence}
                    recurrenceModelChangedCallback={this.updateRecurrence}
                    timeZoneDetails={timeZoneDetails}
                    type={SelfServiceSchedulePaneType.AdminSchedule}
                />
            </div>
        </DialogWidget>;
    }
}
